import { useEffect, useRef, useState } from "react";
import ConversationSidebar from "../components/ConversationSidebar";
import EnvSelector from "../components/EnvSelector";
import LoginForm from "../components/LoginForm";
import MessageBubble from "../components/MessageBubble";
import ChatInput from "../components/ChatInput";
import ModelSettings from "../components/ModelSettings";
import { ApiError, EnvironmentInfo, fetchEnvironments, fetchMe, MeResponse } from "../lib/api";
import { deleteConversation, listConversations } from "../lib/conversations";
import { clearUserToken, getUserToken, setUserToken } from "../lib/storage";
import { useChat } from "../lib/useChat";

export default function ChatPage() {
  const [token, setToken] = useState<string | null>(() => getUserToken());
  const [me, setMe] = useState<MeResponse | null>(null);
  const [checking, setChecking] = useState(true);

  // 页面刷新后用已存的 token 静默复验一次身份，而不是每次都要求重新登录
  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    fetchMe(token)
      .then((info) => setMe(info))
      .catch(() => {
        clearUserToken();
        setToken(null);
      })
      .finally(() => setChecking(false));
  }, [token]);

  function handleLoginSuccess(info: MeResponse, key: string) {
    setUserToken(key);
    setToken(key);
    setMe(info);
  }

  function handleLogout() {
    clearUserToken();
    setToken(null);
    setMe(null);
  }

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center text-slate-400">加载中…</div>;
  }

  if (!token || !me) {
    return (
      <LoginForm
        title="项目诊断助手"
        description="输入你的 API Key 登录（找管理员获取）"
        fieldLabel="API Key"
        placeholder="sk-agent-..."
        submitLabel="登录"
        onSubmit={async (value) => {
          const info = await fetchMe(value);
          handleLoginSuccess(info, value);
        }}
      />
    );
  }

  return <ChatRoom token={token} me={me} onLogout={handleLogout} />;
}

function ChatRoom({
  token,
  me,
  onLogout,
}: {
  token: string;
  me: MeResponse;
  onLogout: () => void;
}) {
  const [envs, setEnvs] = useState<EnvironmentInfo[] | null>(null);
  const [defaultEnv, setDefaultEnv] = useState<string>("");
  const [envError, setEnvError] = useState<string | null>(null);

  useEffect(() => {
    fetchEnvironments(token)
      .then((resp) => {
        setEnvs(resp.environments);
        setDefaultEnv(resp.default_env);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          onLogout();
          return;
        }
        setEnvError(err instanceof Error ? err.message : "加载环境列表失败");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (envError) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-red-600">
        ⚠️ {envError}
      </div>
    );
  }
  if (!envs) {
    return <div className="flex min-h-screen items-center justify-center text-slate-400">加载中…</div>;
  }

  return <ChatRoomInner token={token} me={me} onLogout={onLogout} envs={envs} defaultEnv={defaultEnv} />;
}

function ChatRoomInner({
  token,
  me,
  onLogout,
  envs,
  defaultEnv,
}: {
  token: string;
  me: MeResponse;
  onLogout: () => void;
  envs: EnvironmentInfo[];
  defaultEnv: string;
}) {
  const {
    messages,
    send,
    busy,
    awaitingClarification,
    newConversation,
    loadConversation,
    threadId,
    env,
    setEnv,
  } = useChat(token, me.user_id, defaultEnv, onLogout);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [conversations, setConversations] = useState(() => listConversations(me.user_id));

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // 每轮对话结束后 useChat 已把最新内容写入 localStorage，这里重读一次刷新侧栏
  useEffect(() => {
    setConversations(listConversations(me.user_id));
  }, [me.user_id, threadId, messages]);

  function handleDelete(tid: string) {
    deleteConversation(me.user_id, tid);
    setConversations(listConversations(me.user_id));
    if (tid === threadId) newConversation();
  }

  return (
    <div className="flex h-screen">
      <ConversationSidebar
        conversations={conversations}
        activeThreadId={threadId}
        onSelect={loadConversation}
        onDelete={handleDelete}
        onNew={newConversation}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
          <div>
            <h1 className="text-sm font-semibold text-slate-900">项目诊断助手</h1>
            <p className="text-xs text-slate-400">{me.name}（{me.user_id}）</p>
          </div>
          <div className="flex items-center gap-2">
            <EnvSelector envs={envs} value={env} onChange={setEnv} />
            <button
              onClick={() => setShowSettings(true)}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              模型设置
            </button>
            <button
              onClick={onLogout}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              退出
            </button>
          </div>
        </header>

        {showSettings && (
          <ModelSettings
            token={token}
            onClose={() => setShowSettings(false)}
            onAuthError={onLogout}
          />
        )}

        <main className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {messages.length === 0 && <EmptyState />}
          {messages.map((m) => (
            <MessageBubble key={m.id} message={m} token={token} />
          ))}
          <div ref={bottomRef} />
        </main>

        <ChatInput
          onSend={send}
          disabled={busy}
          placeholder={
            awaitingClarification
              ? "请回答上面的澄清问题…"
              : "问点什么？例如：查昨天支付接口有没有报错"
          }
        />
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="mx-auto mt-16 max-w-md text-center text-sm text-slate-400">
      <p>可以问数据、查日志、查代码，跨源排查问题。</p>
      <p className="mt-1">在上方"环境"下拉框里选好要查的环境，全程按这个环境执行。</p>
    </div>
  );
}
